import { Shield, Network, TrendingUp, Target, Leaf } from 'lucide-react';
import { Link } from 'react-router-dom';

const OurValues = () => {
  const values = [
    {
      icon: <Shield className="w-8 h-8 text-[#007BFF]" />,
      title: 'Integrity & Credibility',
      description: 'We maintain the highest standards of research integrity and analytical independence, founded on rigorous methodologies and transparent assumptions.',
      principles: [
        'Independent analysis free from commercial bias',
        'Transparent research methodologies',
        'Rigorous quality control processes',
        'Commitment to factual accuracy'
      ]
    },
    {
      icon: <Network className="w-8 h-8 text-[#007BFF]" />,
      title: 'Connected Intelligence',
      description: 'Energy systems are interconnected, and effective intelligence requires understanding these linkages across markets, policy, and technology.',
      principles: [
        'Holistic market perspective',
        'Cross-commodity linkage analysis',
        'Integration of technical and commercial factors',
        'Recognition of system-level dynamics'
      ]
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-[#007BFF]" />,
      title: 'Forward Thinking',
      description: 'We focus on forward-looking analysis that anticipates market developments, identifies emerging trends, and assesses future scenarios.',
      principles: [
        'Scenario-based analysis',
        'Trend identification and impact assessment',
        'Forward price curve development',
        'Strategic positioning intelligence'
      ]
    },
    {
      icon: <Target className="w-8 h-8 text-[#007BFF]" />,
      title: 'Analytical Excellence',
      description: 'Delivering exceptional intelligence requires analytical rigor, technical competence, and continuous improvement in methodologies.',
      principles: [
        'Rigorous analytical frameworks',
        'Quantitative and qualitative integration',
        'Continuous methodology enhancement',
        'Technical competence across disciplines'
      ]
    },
    {
      icon: <Leaf className="w-8 h-8 text-[#007BFF]" />,
      title: 'Sustainable Impact',
      description: 'We provide intelligence that supports informed decision-making on transition pathways, investment allocation, and policy frameworks.',
      principles: [
        'Transition pathway analysis',
        'Climate policy impact assessment',
        'Technology adoption economics',
        'Long-term sustainability considerations'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Our Values</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              The principles guiding our intelligence delivery and commitment to clients across research and strategic insight.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section - 5-Column Horizontal Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Our Core Values</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Five foundational principles that define our approach to delivering trusted energy intelligence
            </p>
          </div>

          {/* 5-Column Grid - Desktop: single row, Tablet: 3+2, Mobile: stacked */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="bg-slate-50 rounded-lg border border-slate-200 hover:border-[#007BFF] transition-all duration-300 flex flex-col h-full"
              >
                {/* Value Header */}
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center justify-center w-14 h-14 bg-white rounded-full border-2 border-[#007BFF] mx-auto mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-lg font-bold text-[#1F3C88] text-center leading-tight">
                    {value.title}
                  </h3>
                </div>

                {/* Value Description */}
                <div className="p-6 flex-grow">
                  <p className="text-sm text-slate-600 leading-relaxed text-center">
                    {value.description}
                  </p>
                </div>

                {/* In Practice Section */}
                <div className="p-6 bg-white border-t border-slate-200 mt-auto">
                  <h4 className="text-sm font-semibold text-[#1F3C88] mb-4 text-center">
                    In Practice
                  </h4>
                  <ul className="space-y-2">
                    {value.principles.map((principle, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-xs text-slate-600 leading-relaxed">{principle}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values In Action */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1F3C88] mb-6">Values in Action</h2>
          <p className="text-xl text-slate-700 leading-relaxed">
            These values shape every aspect of our work — from research design and data collection to analysis, quality review, and client delivery. They define who we are as an organization and guide our commitment to providing trusted energy intelligence.
          </p>
        </div>
      </section>
    </div>
  );
};

export default OurValues;
